import { Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { ProductPlaceholder } from "../components/ProductPlaceholder";
import { X, GitCompare, MessageCircle } from "lucide-react";
import { imageUrl, whatsappLink } from "../data/constants";
import { useCompare } from "../context/CompareContext";

const ROWS = [
  ["Type", "category"], ["Size", (p) => (p.sizes || []).join(", ")], ["Colour", (p) => (p.colours || []).join(", ")],
  ["Construction", "construction"], ["Thickness", "thickness"], ["Weight", "weight"], ["Backing", "backing"],
  ["Features", (p) => (p.features || []).join(", ")], ["Application", (p) => (p.applications || []).join(", ")],
];

export default function ComparePage() {
  const { items, remove } = useCompare();
  const val = (p, src) => { const v = typeof src === "function" ? src(p) : p[src]; return v || "—"; };

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6" data-testid="product-comparison-container">
        <div className="mb-8 flex items-center gap-3">
          <GitCompare className="h-7 w-7 text-[#10B981]" />
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#0B1B3D]">Compare Products</h1>
        </div>

        {items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-16 text-center">
            <p className="font-display text-lg font-semibold text-slate-700">No products to compare yet</p>
            <p className="mt-1 text-sm text-slate-500">Add products from the catalogue to compare them side by side.</p>
            <Link to="/catalogue" className="mt-5 inline-block rounded-xl bg-[#0B1B3D] px-6 py-3 text-sm font-bold text-white hover:bg-[#1E293B]">
              Browse Catalogue
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 w-36 bg-slate-50 p-4 text-left font-display text-xs font-bold uppercase tracking-wider text-slate-500">Attribute</th>
                  {items.map((p) => (
                    <th key={p.id} className="min-w-[200px] border-l border-slate-100 p-4 align-top">
                      <div className="relative">
                        <button onClick={() => remove(p.id)} data-testid="comparison-remove-product-btn"
                          className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-slate-400 hover:bg-red-50 hover:text-red-500">
                          <X className="h-3.5 w-3.5" />
                        </button>
                        <div className="aspect-[4/3] overflow-hidden rounded-xl bg-slate-100">
                          {imageUrl(p.primary_image || p.images?.[0], 400) ? (
                            <img src={imageUrl(p.primary_image || p.images?.[0], 400)} alt={p.name} className="h-full w-full object-cover" />
                          ) : <ProductPlaceholder className="h-full w-full" />}
                        </div>
                        <Link to={`/product/${p.id}`} className="mt-2 block font-display text-sm font-semibold text-[#0B1B3D] hover:text-[#059669]">{p.name}</Link>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map(([label, src], ri) => (
                  <tr key={label} className={ri % 2 ? "bg-slate-50/50" : "bg-white"}>
                    <td className="sticky left-0 z-10 bg-inherit p-4 font-semibold text-slate-700">{label}</td>
                    {items.map((p) => (
                      <td key={p.id} className="border-l border-slate-100 p-4 font-spec text-xs text-slate-600">{val(p, src)}</td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td className="sticky left-0 z-10 bg-white p-4" />
                  {items.map((p) => (
                    <td key={p.id} className="border-l border-slate-100 p-4">
                      <a href={whatsappLink({ product: p.name })} target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-1.5 rounded-xl bg-[#25D366] px-3 py-2 text-xs font-bold text-white hover:scale-105">
                        <MessageCircle className="h-3.5 w-3.5" /> Enquire
                      </a>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
